/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import {
  accessAPI,
  downloadFile,
  formatFecha,
  formatMonto,
  Titulo,
  Boton,
  CampoInformacion,
  Sidebar,
  SelectorArchivo,
  conIva,
  useIva,
  usePermiso
} from "../../shared";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import TagExtension from "./TagExtension";
import TagRefacturacion from "./TagRefacturacion";
import { Skeleton } from "primereact/skeleton";
import { confirmDialog } from "primereact/confirmdialog";
import { Divider } from "primereact/divider";
import useConBiller from "./useConBiller";

/**
 * Sidebar reutilizable para mostrar detalle de una factura con acciones.
 *
 * @param {number|null} facturaId - ID de la factura a mostrar (null cierra el sidebar)
 * @param {function} onHide - Callback al cerrar el sidebar
 * @param {function} [onAccion] - Callback tras una acción que cambia estado (marcar pagada, nota de crédito)
 */
export default function SidebarFactura({ facturaId, onHide, onAccion }) {
  // Con lectura se ve el detalle y se descargan los comprobantes; cobrar,
  // anular y subir archivos pide full
  const { puedeEditar } = usePermiso("admin.facturacion");
  const iva = useIva();
  const [factura, setFactura] = useState(null);
  const [loading, setLoading] = useState(false);
  const [enProceso, setEnProceso] = useState(false);

  // Sin Biller los comprobantes los sube el admin. Cada uno se puede subir en
  // cualquier momento: al emitir, al pagar, al anular, o después.
  const conBiller = useConBiller();
  const [subiendo, setSubiendo] = useState(null);

  const toast = useRef(null);

  useEffect(() => {
    if (facturaId) {
      setLoading(true);
      setFactura(null);
      accessAPI(
        "GET",
        `admin/facturacion/${facturaId}`,
        null,
        (respuesta) => {
          setFactura(respuesta);
          setLoading(false);
        },
        (respuesta) => {
          setLoading(false);
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail:
              respuesta[0]?.msg || "Error al cargar la factura",
            sticky: true
          });
        }
      );
    }
  }, [facturaId]);

  function recargarFactura() {
    if (!facturaId) return;
    accessAPI(
      "GET",
      `admin/facturacion/${facturaId}`,
      null,
      (respuesta) => {
        setFactura(respuesta);
        setEnProceso(false);
        onAccion?.();
      },
      () => setEnProceso(false)
    );
  }

  function marcarPagada() {
    confirmDialog({
      message: "¿Marcar esta factura como pagada sin comprobante?",
      header: "Confirmar",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Confirmar",
      rejectLabel: "Cancelar",
      accept: () => {
        setEnProceso(true);
        accessAPI(
          "PUT",
          `admin/facturacion/${facturaId}/pagar`,
          null,
          (respuesta) => {
            toast.current?.show({
              severity: "success",
              summary: "Éxito",
              detail: respuesta[0]?.msg || "Factura marcada como pagada"
            });
            recargarFactura();
          },
          (respuesta) => {
            setEnProceso(false);
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail:
                respuesta[0]?.msg || "Error al marcar factura como pagada",
              sticky: true
            });
          }
        );
      }
    });
  }

  // Acá el comprobante se sube apenas se elige: la factura ya existe, así que
  // no hay nada que esperar.
  function subirComprobante(tipo, archivo) {
    if (!archivo) return;

    setSubiendo(tipo);
    accessAPI(
      "POST",
      `admin/facturacion/${facturaId}/comprobante/${tipo}`,
      null,
      () => {
        setSubiendo(null);
        toast.current?.show({
          severity: "success",
          summary: "Comprobante subido",
          life: 3000
        });
        recargarFactura();
      },
      (respuesta) => {
        setSubiendo(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "No se pudo subir el comprobante",
          sticky: true
        });
      },
      archivo
    );
  }

  function descargarPDF() {
    setEnProceso(true);
    downloadFile(
      `admin/facturacion/${facturaId}/pdf`,
      `Factura-${factura.numeroFactura || facturaId}.pdf`,
      () => setEnProceso(false),
      (respuesta) => {
        setEnProceso(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar factura",
          sticky: true
        });
      }
    );
  }

  function descargarRecibo() {
    setEnProceso(true);
    downloadFile(
      `admin/facturacion/${facturaId}/recibo`,
      `Recibo-${factura.numeroFactura || facturaId}.pdf`,
      () => setEnProceso(false),
      (respuesta) => {
        setEnProceso(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar recibo",
          sticky: true
        });
      }
    );
  }

  function generarNotaCredito() {
    confirmDialog({
      message: conBiller
        ? "¿Emitir una nota de crédito en Biller para anular esta factura por el total? Esta acción no se puede deshacer."
        : "¿Anular esta factura? La nota de crédito la emite el organismo por fuera de Morfeo; después se puede adjuntar acá. Esta acción no se puede deshacer.",
      header: "Confirmar nota de crédito",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Emitir nota de crédito",
      rejectLabel: "Cancelar",
      acceptClassName: "p-button-danger",
      accept: () => {
        setEnProceso(true);
        accessAPI(
          "POST",
          `admin/facturacion/${facturaId}/notacredito`,
          null,
          (respuesta) => {
            toast.current?.show({
              severity: "success",
              summary: "Éxito",
              detail: respuesta[0]?.msg || "Nota de crédito emitida"
            });
            recargarFactura();
          },
          (respuesta) => {
            setEnProceso(false);
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail:
                respuesta[0]?.msg || "Error al emitir nota de crédito",
              sticky: true
            });
          }
        );
      }
    });
  }

  function descargarNotaCredito() {
    setEnProceso(true);
    downloadFile(
      `admin/facturacion/${facturaId}/notacredito`,
      `NotaDeCredito-${factura.numeroFactura || facturaId}.pdf`,
      () => setEnProceso(false),
      (respuesta) => {
        setEnProceso(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0]?.msg || "Error al descargar nota de crédito",
          sticky: true
        });
      }
    );
  }

  const estadoTag = factura
    ? factura.anulada
      ? <Tag value="Anulada" severity="danger" />
      : factura.fechaPago
        ? <Tag value="Pagada" severity="success" />
        : <Tag value="Pendiente de pago" severity="warning" />
    : null;

  const pendiente = factura && !factura.fechaPago && !factura.anulada;

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Sidebar
        visible={!!facturaId}
        onHide={onHide}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        {loading && (
          <div>
            <Skeleton width="60%" height="2rem" css={{ marginBottom: "1em" }} />
            {[1, 2, 3, 4].map((i) => (
              <div key={i} css={{ marginBottom: "1em" }}>
                <Skeleton width="40%" height="0.9rem" css={{ marginBottom: "0.3rem" }} />
                <Skeleton width="70%" height="1.1rem" />
              </div>
            ))}
          </div>
        )}
        {!loading && factura && (
          <div>
            <Titulo>{factura.numeroFactura || `Factura #${factura.id}`}</Titulo>

            <div css={{ marginBottom: "1em" }}>{estadoTag}</div>

            <div className="grid">
              <CampoInformacion nombreCampo="Cliente">
                {factura.cliente?.razonsocial || "-"}
              </CampoInformacion>
              {factura.clienteFacturado && (
                <CampoInformacion nombreCampo="Facturado a">
                  {factura.clienteFacturado.razonsocial}
                </CampoInformacion>
              )}
              <CampoInformacion nombreCampo="Monto">
                {factura.moneda?.simbolo} {formatMonto(factura.monto)}
              </CampoInformacion>
              {factura.masIva && (
                <CampoInformacion nombreCampo="Monto con IVA">
                  {factura.moneda?.simbolo} {formatMonto(conIva(factura.monto, iva))}
                </CampoInformacion>
              )}
              <CampoInformacion nombreCampo="Moneda">
                {factura.moneda?.nombre || "-"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Fecha de emisión">
                {formatFecha(factura.fechaCreacion)}
              </CampoInformacion>
              {factura.fechaPago && (
                <CampoInformacion nombreCampo="Fecha de pago">
                  {formatFecha(factura.fechaPago)}
                </CampoInformacion>
              )}
            </div>

            {/* Propuestas comerciales */}
            {factura.propuestas?.length > 0 && (
              <>
                <Titulo>Propuestas comerciales</Titulo>
                {factura.propuestas.map((p, index) => (
                  <div
                    key={p.id}
                    css={{
                      padding: "0.5em 0",
                      borderBottom:
                        factura.propuestas.length > 1 &&
                        index < factura.propuestas.length - 1
                          ? "1px solid #e0e0e0"
                          : "none",
                      cursor: "pointer",
                      "&:hover": { color: "var(--principal)" }
                    }}
                    onClick={() => window.open(`/propuesta/${p.id}`, "_blank")}
                  >
                    {p.solicitud?.nombrefantasia || `Propuesta #${p.id}`}
                  </div>
                ))}
              </>
            )}

            {/* Procesos */}
            {factura.procesos?.length > 0 && (
              <>
                <Titulo>Procesos</Titulo>
                {factura.procesos.map((proceso, index) => (
                  <div
                    key={index}
                    css={{
                      padding: "0.5em 0",
                      borderBottom:
                        factura.procesos.length > 1 &&
                        index < factura.procesos.length - 1
                          ? "1px solid #e0e0e0"
                          : "none",
                      cursor: proceso.procesoid ? "pointer" : "default",
                      "&:hover": proceso.procesoid
                        ? { color: "var(--principal)" }
                        : {}
                    }}
                    onClick={() => {
                      if (proceso.procesoid) {
                        window.open(`/proceso/${proceso.procesoid}`, "_blank");
                      }
                    }}
                  >
                    {proceso.descripcion}
                    {proceso.origen === "extension" && (
                      <TagExtension />
                    )}
                    {proceso.origen === "refacturacion" && (
                      <TagRefacturacion />
                    )}
                  </div>
                ))}
              </>
            )}

            {/* Acciones */}
            <Divider />

            {puedeEditar && conBiller === false && !factura.archivoFacturaId && (
              <>
                <SelectorArchivo
                  icono="pi pi-upload"
                  etiqueta="Subir factura"
                  disabled={subiendo !== null}
                  onSeleccion={(a) => subirComprobante("factura", a)}
                />
                <Divider />
              </>
            )}

            <div css={{ display: "flex", flexDirection: "column", gap: "0.5em" }}>
              {(factura.idFacturaBiller || factura.archivoFacturaId) && (
                <Boton
                  callback={descargarPDF}
                  transparente
                  loader={enProceso}
                >
                  <i className="pi pi-download" css={{ marginRight: "0.5em" }} />
                  Descargar factura
                </Boton>
              )}
              {puedeEditar && pendiente && (
                <Boton
                  callback={marcarPagada}
                  loader={enProceso}
                >
                  <i className="pi pi-check" css={{ marginRight: "0.5em" }} />
                  Marcar como pagada
                </Boton>
              )}
              {(factura.idReciboBiller || factura.archivoComprobantePagoId) && (
                <Boton
                  callback={descargarRecibo}
                  transparente
                  loader={enProceso}
                >
                  <i className="pi pi-download" css={{ marginRight: "0.5em" }} />
                  Descargar comprobante de pago
                </Boton>
              )}
              {puedeEditar && pendiente && (factura.idFacturaBiller || conBiller === false) && (
                <Boton
                  callback={generarNotaCredito}
                  transparente
                  loader={enProceso}
                >
                  <i className="pi pi-times-circle" css={{ marginRight: "0.5em" }} />
                  Generar nota de crédito
                </Boton>
              )}
              {(factura.idNotaDeCreditoBiller || factura.archivoNotaCreditoId) && (
                <Boton
                  callback={descargarNotaCredito}
                  transparente
                  loader={enProceso}
                >
                  <i className="pi pi-download" css={{ marginRight: "0.5em" }} />
                  Descargar nota de crédito
                </Boton>
              )}

              {/* Los otros dos comprobantes se suben una vez que la factura
                  llegó a ese estado: el de pago solo si está paga, y la nota de
                  crédito solo si está anulada. */}
              {puedeEditar && conBiller === false && (
                <>
                  {factura.fechaPago && !factura.archivoComprobantePagoId && (
                    <SelectorArchivo
                      icono="pi pi-upload"
                      etiqueta="Subir comprobante de pago"
                      disabled={subiendo !== null}
                      onSeleccion={(a) => subirComprobante("pago", a)}
                    />
                  )}
                  {factura.anulada && !factura.archivoNotaCreditoId && (
                    <SelectorArchivo
                      icono="pi pi-upload"
                      etiqueta="Subir nota de crédito"
                      disabled={subiendo !== null}
                      onSeleccion={(a) => subirComprobante("notacredito", a)}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </Sidebar>
    </>
  );
}
